import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface ActivationCodeDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (code: string) => Promise<void>;
    title?: string;
    description?: string;
}

export default function ActivationCodeDialog({
    open,
    onOpenChange,
    onSubmit,
    title = 'Activation Code',
    description = 'Enter your activation code to continue.',
}: ActivationCodeDialogProps) {
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!code.trim()) return;
        setSubmitting(true);
        try {
            await onSubmit(code.trim());
            setCode('');
            onOpenChange(false);
        } catch {
            // caller handles error toast
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!submitting) { onOpenChange(v); setCode(''); } }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-2">
                        <Label htmlFor="activation-code">Activation Code</Label>
                        <Input
                            id="activation-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="Enter code"
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                            disabled={submitting}
                            autoFocus
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" onClick={() => { onOpenChange(false); setCode(''); }} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!code.trim() || submitting}>
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Submitting...
                            </>
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
